import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Share2, Copy } from 'lucide-react';
import FrozenInvitation from '@/components/FrozenInvitation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicInvitationView = () => {
  const { eventId } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rsvpDialogOpen, setRsvpDialogOpen] = useState(false);
  const [rsvpData, setRsvpData] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    attending: true,
    num_guests: 1,
    message: ''
  });

  useEffect(() => {
    fetchInvitation();
  }, [eventId]);

  const fetchInvitation = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/invitation`);
      setInvitation(response.data);
    } catch (error) {
      console.error('Error fetching invitation:', error);
      toast.error('Invitation not found');
    } finally {
      setLoading(false);
    }
  };

  const handleShareInvitation = () => {
    const inviteUrl = `${window.location.origin}/i/${eventId}`;
    const message = `🎉 You're invited to ${invitation.event_title}! 🎉

Join us for ${invitation.person_names}'s ${invitation.event_type}!

📅 Date: ${invitation.date_from}
⏰ Time: ${invitation.time_from}
📍 Venue: ${invitation.venue}

View the full invitation: ${inviteUrl}`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCopyLink = () => {
    const inviteUrl = `${window.location.origin}/i/${eventId}`;
    navigator.clipboard.writeText(inviteUrl);
    toast.success('Invitation link copied!');
  };

  const handleSubmitRSVP = async (e) => {
    e.preventDefault();

    if (!rsvpData.guest_name) {
      toast.error('Please enter your name');
      return;
    }

    try {
      await axios.post(`${API}/events/${eventId}/rsvp`, rsvpData);
      toast.success(rsvpData.attending ? 'RSVP confirmed! We can\'t wait to see you!' : 'Thank you for letting us know.');
      setRsvpDialogOpen(false);
      setRsvpData({
        guest_name: '',
        guest_email: '',
        guest_phone: '',
        attending: true,
        num_guests: 1,
        message: ''
      });
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      toast.error('Failed to submit RSVP');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Invitation not found</p>
      </div>
    );
  }

  return (
    <>
      <FrozenInvitation 
        invitation={invitation} 
        showWishlistButton={true} 
        eventId={eventId}
        onRSVPClick={() => setRsvpDialogOpen(true)}
      />

      {/* Share Buttons - Floating */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        <Button
          onClick={handleShareInvitation}
          size="lg"
          className="gap-2 shadow-xl"
          style={{background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)'}}
        >
          <Share2 className="w-5 h-5" />
          <span className="hidden sm:inline">Share via WhatsApp</span>
        </Button>
        <Button
          onClick={handleCopyLink}
          variant="outline"
          size="lg"
          className="gap-2 shadow-xl bg-white"
        >
          <Copy className="w-4 h-4" />
          <span className="hidden sm:inline">Copy Link</span>
        </Button>
      </div>

      {/* RSVP Dialog */}
      <Dialog open={rsvpDialogOpen} onOpenChange={setRsvpDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>RSVP to {invitation.person_names}'s {invitation.event_type} ❄️</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitRSVP} className="space-y-4">
            <div>
              <Label htmlFor="guest_name">Your Name *</Label>
              <Input
                id="guest_name"
                placeholder="John Doe"
                value={rsvpData.guest_name}
                onChange={(e) => setRsvpData({ ...rsvpData, guest_name: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="guest_email">Email (Optional)</Label>
              <Input
                id="guest_email"
                type="email"
                placeholder="john@example.com"
                value={rsvpData.guest_email}
                onChange={(e) => setRsvpData({ ...rsvpData, guest_email: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="guest_phone">Phone (Optional)</Label>
              <Input
                id="guest_phone"
                type="tel"
                placeholder="+91 XXXXXXXXXX"
                value={rsvpData.guest_phone}
                onChange={(e) => setRsvpData({ ...rsvpData, guest_phone: e.target.value })}
              />
            </div>

            <div>
              <Label>Will you attend?</Label>
              <div className="flex gap-4 mt-2">
                <Button
                  type="button"
                  onClick={() => setRsvpData({ ...rsvpData, attending: true })}
                  variant={rsvpData.attending ? 'default' : 'outline'}
                  className="flex-1"
                >
                  ✅ Yes, I'll be there!
                </Button>
                <Button
                  type="button"
                  onClick={() => setRsvpData({ ...rsvpData, attending: false })}
                  variant={!rsvpData.attending ? 'default' : 'outline'}
                  className="flex-1"
                >
                  ❌ Sorry, can't make it
                </Button>
              </div>
            </div>

            {rsvpData.attending && (
              <div>
                <Label htmlFor="num_guests">Number of Guests</Label>
                <Input
                  id="num_guests"
                  type="number"
                  min="1"
                  value={rsvpData.num_guests}
                  onChange={(e) => setRsvpData({ ...rsvpData, num_guests: parseInt(e.target.value) })}
                />
              </div>
            )}

            <div>
              <Label htmlFor="message">Message (Optional)</Label>
              <textarea
                id="message"
                placeholder="Leave a special message..."
                value={rsvpData.message}
                onChange={(e) => setRsvpData({ ...rsvpData, message: e.target.value })}
                className="w-full p-2 border rounded-md min-h-[80px]"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full"
              style={{background: 'linear-gradient(135deg, #a855f7 0%, #3b82f6 100%)'}}
            >
              Submit RSVP
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PublicInvitationView;
